/**
 * Conflict Resolution System
 * 
 * Handles timestamp-based conflict detection and resolution with
 * automatic resolution strategies and manual fallback UI support.
 */

import { indexedDBService } from '../indexeddb';
import { DataTransformUtils } from './DataTransformer';
import type { ConflictResolution } from '../indexeddb/schema';

/**
 * Conflict types that can occur during sync
 */
export type ConflictType = 
  | 'update_conflict'    // Both local and remote have been updated
  | 'delete_conflict'    // One side deleted, other side updated
  | 'create_conflict';   // Same ID created on both sides

/**
 * Resolution strategies for conflicts
 */
export type ResolutionStrategy =
  | 'local_wins'         // Keep local version
  | 'remote_wins'        // Keep remote version
  | 'manual'             // Require manual resolution
  | 'merge'              // Attempt to merge changes (future)
  | 'timestamp_latest'   // Use timestamp to determine winner
  | 'custom';            // Custom resolution chosen by user

/**
 * Conflict data structure
 */
export interface Conflict {
  id: string;
  tableName: string;
  recordId: string;
  conflictType: ConflictType;
  localData: Record<string, unknown>;
  remoteData: Record<string, unknown>;
  localTimestamp: string;
  remoteTimestamp: string;
  createdAt: string;
  resolved: boolean;
  resolutionStrategy?: ResolutionStrategy;
  resolvedAt?: string;
  resolvedData?: Record<string, unknown>;
}

/**
 * Conflict resolution result
 */
export interface ConflictResolutionResult {
  resolved: boolean;
  strategy: ResolutionStrategy;
  finalData: Record<string, unknown> | null;
  requiresManualResolution: boolean;
}

/**
 * Conflict Resolver Service
 */
export class ConflictResolver {
  private defaultStrategy: ResolutionStrategy = 'timestamp_latest';

  constructor(defaultStrategy: ResolutionStrategy = 'timestamp_latest') {
    this.defaultStrategy = defaultStrategy;
  }

  /**
   * Detect conflicts between local and remote data
   */
  async detectConflict(
    tableName: string,
    recordId: string,
    localData: Record<string, unknown>,
    remoteData: Record<string, unknown>
  ): Promise<Conflict | null> {
    // If either record doesn't exist, no conflict
    if (!localData || !remoteData) {
      return null;
    }

    const localTimestamp = DataTransformUtils.getLastModified(localData);
    const remoteTimestamp = DataTransformUtils.getLastModified(remoteData);

    // Check if both have been modified
    const localHasChanges = DataTransformUtils.hasOfflineChanges(localData);
    const remoteIsNewer = new Date(remoteTimestamp) > new Date(localTimestamp);

    // Conflict exists if local has changes and remote is newer
    if (localHasChanges && remoteIsNewer) {
      const conflict: Conflict = {
        id: crypto.randomUUID(),
        tableName,
        recordId,
        conflictType: 'update_conflict',
        localData,
        remoteData,
        localTimestamp,
        remoteTimestamp,
        createdAt: new Date().toISOString(),
        resolved: false
      };

      // Store conflict for manual resolution if needed
      await this.storeConflict(conflict);
      return conflict;
    }

    return null;
  }

  /**
   * Resolve a conflict using the specified strategy
   */
  async resolveConflict(
    conflict: Conflict,
    strategy?: ResolutionStrategy
  ): Promise<ConflictResolutionResult> {
    const resolutionStrategy = strategy || this.defaultStrategy;

    try {
      let finalData: Record<string, unknown> | null;
      let requiresManualResolution = false;

      switch (resolutionStrategy) {
        case 'local_wins':
          finalData = conflict.localData;
          break;

        case 'remote_wins':
          finalData = conflict.remoteData;
          break;

        case 'timestamp_latest':
          finalData = this.resolveByTimestamp(conflict);
          break;

        case 'merge': {
          const mergeResult = this.attemptMerge(conflict);
          finalData = mergeResult.data;
          requiresManualResolution = mergeResult.requiresManual;
          break;
        }

        case 'manual':
        default:
          requiresManualResolution = true;
          finalData = null;
          break;
      }

      // Update conflict record
      if (!requiresManualResolution) {
        await this.markConflictResolved(conflict.id, resolutionStrategy, finalData || {});
      }

      return {
        resolved: !requiresManualResolution,
        strategy: resolutionStrategy,
        finalData,
        requiresManualResolution
      };
    } catch (error) {
      console.error('Error resolving conflict:', error);
      return {
        resolved: false,
        strategy: resolutionStrategy,
        finalData: null,
        requiresManualResolution: true
      };
    }
  }

  /**
   * Resolve conflict by timestamp (latest wins)
   */
  private resolveByTimestamp(conflict: Conflict): Record<string, unknown> {
    const localTime = new Date(conflict.localTimestamp);
    const remoteTime = new Date(conflict.remoteTimestamp);

    if (localTime > remoteTime) {
      console.log(`🕒 Conflict resolved: Local wins (${conflict.localTimestamp} > ${conflict.remoteTimestamp})`);
      return conflict.localData;
    } else {
      console.log(`🕒 Conflict resolved: Remote wins (${conflict.remoteTimestamp} > ${conflict.localTimestamp})`);
      return conflict.remoteData;
    }
  }

  /**
   * Attempt to merge changes (basic implementation)
   */
  private attemptMerge(conflict: Conflict): { data: Record<string, unknown> | null; requiresManual: boolean } {
    try {
      // Simple merge strategy: combine non-conflicting fields
      const merged = { ...conflict.remoteData };
      
      // For specific fields that can be safely merged
      const mergeableFields = ['notes', 'techniques_practiced'];
      
      for (const field of mergeableFields) {
        if (conflict.localData[field] && conflict.remoteData[field]) {
          if (Array.isArray(conflict.localData[field])) {
            // Merge arrays, removing duplicates
            const localArray = (conflict.localData[field] as unknown[]) || [];
            const remoteArray = (conflict.remoteData[field] as unknown[]) || [];
            merged[field] = [...new Set([...remoteArray, ...localArray])];
          } else if (typeof conflict.localData[field] === 'string') {
            // For text fields, prefer local if it's longer (more content)
            if ((conflict.localData[field] as string).length > (conflict.remoteData[field] as string).length) {
              merged[field] = conflict.localData[field];
            }
          }
        }
      }

      // Use latest timestamp
      merged.updated_at = conflict.localTimestamp > conflict.remoteTimestamp
        ? conflict.localTimestamp
        : conflict.remoteTimestamp;

      return { data: merged, requiresManual: false };
    } catch (error) {
      console.error('Merge attempt failed:', error);
      return { data: null, requiresManual: true };
    }
  }

  /**
   * Store conflict for manual resolution
   */
  private async storeConflict(conflict: Conflict): Promise<void> {
    const conflictRecord: ConflictResolution = {
      id: conflict.id,
      table_name: conflict.tableName,
      record_id: conflict.recordId,
      local_data: conflict.localData,
      remote_data: conflict.remoteData,
      conflict_type: conflict.conflictType,
      created_at: conflict.createdAt,
      resolved: false
    };

    await indexedDBService.offlineDB.put('conflict_resolution', conflictRecord);
    console.log(`⚠️ Conflict stored for manual resolution: ${conflict.tableName}/${conflict.recordId}`);
  }

  /**
   * Mark conflict as resolved
   */
  private async markConflictResolved(
    conflictId: string,
    strategy: ResolutionStrategy,
    _resolvedData: Record<string, unknown>
  ): Promise<void> {
    const conflict = await indexedDBService.offlineDB.get<ConflictResolution>('conflict_resolution', conflictId);
    
    if (conflict) {
      conflict.resolved = true;
      conflict.resolution_strategy = strategy;
      conflict.resolved_at = new Date().toISOString();
      
      await indexedDBService.offlineDB.put('conflict_resolution', conflict);
      console.log(`✅ Conflict resolved using ${strategy}: ${conflict.table_name}/${conflict.record_id}`);
    }
  }

  /**
   * Get all unresolved conflicts
   */
  async getUnresolvedConflicts(): Promise<ConflictResolution[]> {
    const allConflicts = await indexedDBService.offlineDB.getAll<ConflictResolution>('conflict_resolution');
    return allConflicts.filter(conflict => !conflict.resolved);
  }

  /**
   * Get conflicts for a specific table
   */
  async getConflictsForTable(tableName: string): Promise<ConflictResolution[]> {
    return indexedDBService.offlineDB.queryByIndex<ConflictResolution>(
      'conflict_resolution',
      'by_table_name',
      tableName
    );
  }

  /**
   * Manually resolve a conflict
   */
  async manuallyResolveConflict(
    conflictId: string,
    chosenData: Record<string, unknown>,
    strategy: 'local_wins' | 'remote_wins' | 'custom' = 'custom'
  ): Promise<void> {
    const conflict = await indexedDBService.offlineDB.get<ConflictResolution>('conflict_resolution', conflictId);
    
    if (!conflict) {
      throw new Error(`Conflict not found: ${conflictId}`);
    }

    // Update the record with chosen data
    const tableName = conflict.table_name;
    const recordId = conflict.record_id;

    // Add sync metadata to chosen data
    const finalData = DataTransformUtils.addOfflineMetadata(chosenData, 'pending', false);
    
    // Update the record in IndexedDB
    await indexedDBService.offlineDB.put(tableName, finalData);

    // Mark conflict as resolved
    await this.markConflictResolved(conflictId, strategy, finalData);

    console.log(`👤 Conflict manually resolved: ${tableName}/${recordId}`);
  }

  /**
   * Clear resolved conflicts (cleanup)
   */
  async clearResolvedConflicts(olderThanDays = 7): Promise<number> {
    const cutoffDate = new Date(Date.now() - olderThanDays * 24 * 60 * 60 * 1000);
    const allConflicts = await indexedDBService.offlineDB.getAll<ConflictResolution>('conflict_resolution');
    
    const toDelete = allConflicts.filter(conflict => 
      conflict.resolved && 
      conflict.resolved_at && 
      new Date(conflict.resolved_at) < cutoffDate
    );

    for (const conflict of toDelete) {
      await indexedDBService.offlineDB.delete('conflict_resolution', conflict.id);
    }

    console.log(`🧹 Cleared ${toDelete.length} resolved conflicts older than ${olderThanDays} days`);
    return toDelete.length;
  }

  /**
   * Get conflict statistics
   */
  async getConflictStats(): Promise<{
    total: number;
    unresolved: number;
    resolved: number;
    byTable: Record<string, number>;
  }> {
    const allConflicts = await indexedDBService.offlineDB.getAll<ConflictResolution>('conflict_resolution');
    
    const stats = {
      total: allConflicts.length,
      unresolved: allConflicts.filter(c => !c.resolved).length,
      resolved: allConflicts.filter(c => c.resolved).length,
      byTable: {} as Record<string, number>
    };

    // Count by table
    for (const conflict of allConflicts) {
      stats.byTable[conflict.table_name] = (stats.byTable[conflict.table_name] || 0) + 1;
    }

    return stats;
  }
}

// Export singleton instance
export const conflictResolver = new ConflictResolver();
